/**
 * KAN-944: виден ли клиент текущему пользователю (карточка в приложении
 * менеджера). Запрос при открытии, при возврате в окно и раз в 30 с, пока
 * карточка на экране; сразу перезапрос, когда любой запрос по этому клиенту
 * вернул 404 (сигнал перехватчика apiClient, lib/apiNotFound).
 *   takenBy - имя менеджера, который взял клиента (карточка закрыта);
 *   notFound - доступ ответил 404 (клиента нет или он чужой организации);
 *   checking - запрос доступа идёт (в том числе перезапрос).
 * Запрос закрыт правилами менеджера (useCanCallApi) - хук молчит, карточка как была.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { isNotFoundError } from '@/hooks/useCatalog';
import { onApiNotFound } from '@/lib/apiNotFound';
import { clientAccessApi, clientAccessUrl, type ClientAccess } from '@/services/api/clientAccess';

export const CLIENT_ACCESS_POLL_MS = 30_000;

export const clientAccessQueryKey = (clientId: string) => ['client-access', clientId] as const;

/** Url относится к клиенту (сегмент /<id>/), но это не сам запрос доступа. */
function isClientRequest(url: string, clientId: string): boolean {
  return url.includes(`/${clientId}/`) && !url.includes(clientAccessUrl(clientId));
}

export interface ClientAccessState {
  takenBy: string | null;
  notFound: boolean;
  isLoading: boolean;
  checking: boolean;
}

export function useClientAccess(clientId: string): ClientAccessState {
  const allowed = useCanCallApi('GET', clientAccessUrl(clientId));
  const enabled = allowed && !!clientId;
  const query = useQuery<ClientAccess>({
    queryKey: clientAccessQueryKey(clientId),
    queryFn: () => clientAccessApi.get(clientId),
    enabled,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchInterval: CLIENT_ACCESS_POLL_MS,
  });

  const { refetch } = query;
  useEffect(() => {
    if (!enabled) return undefined;
    return onApiNotFound((url) => {
      if (isClientRequest(url, clientId)) void refetch({ cancelRefetch: false });
    });
  }, [enabled, clientId, refetch]);

  const access = query.data;
  const takenBy = !query.isError && access && !access.visible ? (access.taken_by?.name ?? null) : null;
  return {
    takenBy,
    notFound: isNotFoundError(query.error),
    isLoading: enabled && query.isLoading,
    checking: enabled && query.isFetching,
  };
}
